<template>
  <PreAuthPage class="onb" card-class="onb__card">
    <section class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="48" />
      <h1 class="sh-card__title">{{ isCompany ? t("onboarding.company.title") : t("onboarding.title") }}</h1>
      <p class="sh-card__subtitle">
        {{ isCompany ? t("onboarding.company.subtitle", { name: tenantName }) : t("onboarding.subtitle", { name: tenantName }) }}
      </p>
      <p v-if="email" class="sh-whoami">{{ t("common.signedInAs") }} <b>{{ email }}</b></p>
    </section>

    <!-- A claim a human has to decide. There is nothing to sign into yet, so the
         reference IS the outcome — never a dead end, never a silent failure. -->
    <div v-if="review" class="onb__review" role="status">
      <v-icon size="20" color="info">schedule</v-icon>
      <div>
        <p class="onb__review-title">{{ t("onboarding.reviewTitle") }}</p>
        <p class="onb__review-body">{{ isCompany ? t("onboarding.company.reviewBody") : t("onboarding.reviewBody") }}</p>
        <p class="onb__ref">{{ review }}</p>
      </div>
    </div>

    <!-- PAC-1374: a WMS front door (signupForm "COMPANY") asks for the company
         instead of the farm. Same frame, same review panel, same way out. -->
    <template v-else-if="isCompany">
      <CompanySignupForm @review="onCompanyReview" />
      <p class="onb__foot">{{ t("onboarding.company.footNote") }}</p>
      <v-btn class="onb__switch" variant="text" size="small" block @click="session.signOut()">{{
        t("auth.differentAccountSignIn")
      }}</v-btn>
    </template>

    <template v-else>
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ error }}</span>
      </p>

      <form class="onb__form" @submit.prevent="submit">
        <!-- The ONLY required field. Every extra required box on a sign-up form
             is growers lost at it; the rest are useful, not mandatory. -->
        <v-text-field
          v-model="farmName"
          :label="t('onboarding.farmName')"
          :hint="t('onboarding.farmNameHint')"
          persistent-hint
          variant="outlined"
          density="comfortable"
          autocomplete="organization"
          autofocus
          :disabled="busy"
        />

        <v-text-field
          v-model="contactName"
          :label="t('onboarding.contactName')"
          variant="outlined"
          density="comfortable"
          autocomplete="name"
          :disabled="busy"
          hide-details
        />

        <v-text-field
          v-model="contactPhone"
          :label="t('onboarding.contactPhone')"
          :hint="t('onboarding.contactPhoneHint')"
          persistent-hint
          type="tel"
          variant="outlined"
          density="comfortable"
          autocomplete="tel"
          inputmode="tel"
          :disabled="busy"
        />

        <v-select
          v-model="province"
          :items="provinces"
          :label="t('onboarding.province')"
          variant="outlined"
          density="comfortable"
          clearable
          :disabled="busy"
          hide-details
        />

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          class="onb__submit"
          :loading="busy"
          :disabled="!canSubmit"
          >{{ t("onboarding.submit") }}</v-btn
        >
      </form>

      <p class="onb__foot">{{ t("onboarding.footNote") }}</p>
      <v-btn class="onb__switch" variant="text" size="small" block @click="session.signOut()">{{
        t("auth.differentAccountSignIn")
      }}</v-btn>
    </template>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * DEV-937 — the one screen between an Auth0 sign-in and a working farm.
 *
 * Reached only from `status === "needs-org"`: signed in, no organisation, and
 * this tenant app accepts sign-ups. On any other app an orgless visitor still
 * lands on NoAccess, which is correct there — a stranger must not be able to
 * mint an organisation inside a white-label customer's tenant.
 *
 * Deliberately ONE required field. The bar Devin set was "seamless and clean",
 * and every extra mandatory box on a sign-up form is growers lost at it. Name,
 * phone and province are useful for the follow-up call, not gates.
 *
 * There is no AgriOps step here on purpose. A verified claim can only be made
 * by the backend driving AgriOps itself (`POST /boer/agriops/signup`); a
 * business id supplied by a form would let anyone claim any organisation that
 * has no members. This form always creates a fresh, unverified organisation,
 * and connecting AgriOps afterwards is what verifies it.
 *
 * PAC-1374: on a WMS front door (`tenant.signupForm === "COMPANY"`) the body
 * is CompanySignupForm instead — legal name, registration/VAT, country, base
 * currency — and success lands in WMS on "add your first site".
 */
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import CompanySignupForm from "@/shell/views/CompanySignupForm.vue";

/** ZA provinces. The app is per-country, so this list travels with the tenant. */
const ZA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

export default defineComponent({
  name: "Onboarding",
  components: { CompanySignupForm, LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();

    const farmName = ref("");
    const contactName = ref("");
    const contactPhone = ref("");
    const province = ref<string | null>(null);
    const busy = ref(false);
    const error = ref<string | null>(null);
    const review = ref<string | null>(null);

    const canSubmit = computed(() => farmName.value.trim().length > 1 && !busy.value);

    // PAC-1374: the form is the tenant app's choice. Anything but an explicit
    // "COMPANY" (an older backend sends nothing) is the farm form — Boer as before.
    const isCompany = computed(() => session.tenant?.signupForm === "COMPANY");

    /** A company review always shows the panel, even if the task (and so the reference) could not be written. */
    function onCompanyReview(reference: string): void {
      review.value = reference || t("onboarding.company.noReference");
    }

    async function submit(): Promise<void> {
      if (!canSubmit.value) return;
      busy.value = true;
      error.value = null;
      try {
        const result = await session.completeSignup({
          farmName: farmName.value.trim(),
          contactName: contactName.value.trim() || undefined,
          contactPhone: contactPhone.value.trim() || undefined,
          province: province.value || undefined,
        });
        // CREATED/CLAIMED: resolveOrgAccess already moved the session on, and
        // App.vue swaps this view out. Only a review has anything left to say.
        if (result.outcome === "NEEDS_REVIEW") review.value = result.reference;
      } catch (e) {
        // The server's message is written for the grower (an unverified email,
        // a rate limit, an app that does not accept sign-ups), so show it
        // rather than a generic one — but never leave them with nothing.
        error.value = (e as Error)?.message?.replace(/^POST \S+ failed: \d+\s*/, "") || t("onboarding.failed");
      } finally {
        busy.value = false;
      }
    }

    return {
      t,
      session,
      farmName,
      contactName,
      contactPhone,
      province,
      provinces: ZA_PROVINCES,
      busy,
      error,
      review,
      canSubmit,
      submit,
      isCompany,
      onCompanyReview,
      tenantName: computed(() => session.tenant?.name || "Boer"),
      branding: computed(() => session.tenant?.branding ?? null),
      email: computed(() => session.user?.email || null),
    };
  },
});
</script>

<style scoped>
.onb__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.onb__submit {
  margin-top: 4px;
  font-weight: 650;
}
.onb__foot {
  font-size: 0.72rem;
  color: var(--sh-text-60);
  margin: 14px 0 0;
  text-align: center;
  line-height: 1.5;
}
.onb__switch {
  margin-top: 6px;
  font-size: 12px;
  color: var(--sh-text-60);
}
.onb__review {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 10px;
  background: rgba(var(--v-theme-info), 0.08);
}
.onb__review-title {
  font-weight: 650;
  margin: 0 0 4px;
}
.onb__review-body {
  font-size: 0.8rem;
  line-height: 1.55;
  margin: 0;
  color: var(--sh-text-60);
}
.onb__ref {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin: 10px 0 0;
}
</style>
